// Allowlist cleaner for stored CKEditor markup, matching the backend HtmlSanitizerHelper rules so a
// row saved before the save-side sanitizer existed renders exactly as a newly saved one would.
const ALLOWED_TAGS = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup',
  'a', 'ul', 'ol', 'li', 'blockquote',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'caption', 'colgroup', 'col', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
  'figure', 'figcaption', 'oembed',
  'img', 'span', 'div', 'hr', 'pre', 'code',
]);

const DANGEROUS_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'applet', 'param',
  'link', 'meta', 'base', 'title', 'noscript', 'template',
  'form', 'input', 'button', 'select', 'option', 'optgroup', 'textarea', 'label', 'fieldset',
  'frame', 'frameset', 'portal',
  'svg', 'math', 'foreignobject', 'canvas',
  'audio', 'video', 'source', 'track',
]);

const GLOBAL_ATTRIBUTES = new Set(['class', 'style', 'dir']);

const TAG_ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(['href', 'title', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height']),
  oembed: new Set(['url']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan', 'scope']),
  col: new Set(['span']),
  colgroup: new Set(['span']),
  ol: new Set(['start', 'type']),
  li: new Set(['value']),
};

const URL_ATTRIBUTES = new Set(['href', 'src', 'url']);
const ALLOWED_URL_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);
const DATA_IMAGE_URL = /^data:image\/(png|jpeg|jpg|gif|webp);base64,[a-z0-9+/=]+$/i;
const BLOCKED_CSS_PROPERTIES = new Set(['behavior', '-moz-binding', 'binding', 'expression', '-o-link', '-o-link-source']);
const BLOCKED_CSS_VALUES = ['expression(', 'javascript:', 'vbscript:', '-moz-binding', 'behavior:'];
const MAX_DEPTH = 100;

export function sanitizeRichText(html: string): string {
  if (!html || !html.trim()) {
    return '';
  }
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  sanitizeChildren(body, 0);
  return body.innerHTML;
}

function sanitizeChildren(parent: Node, depth: number): void {
  Array.from(parent.childNodes).forEach(child => {
    if (child.nodeType === Node.COMMENT_NODE || child.nodeType === Node.PROCESSING_INSTRUCTION_NODE) {
      child.remove();
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const element = child as Element;
    const tag = element.localName.toLowerCase();
    if (depth >= MAX_DEPTH || DANGEROUS_TAGS.has(tag)) {
      element.remove();
      return;
    }
    sanitizeChildren(element, depth + 1);
    if (ALLOWED_TAGS.has(tag)) {
      sanitizeAttributes(element, tag);
    } else {
      element.replaceWith(...Array.from(element.childNodes));
    }
  });
}

function sanitizeAttributes(element: Element, tag: string): void {
  const tagSpecific = TAG_ATTRIBUTES[tag];
  Array.from(element.attributes).forEach(({ name, value }) => {
    const lower = name.toLowerCase();
    const allowed = !lower.startsWith('on') && (GLOBAL_ATTRIBUTES.has(lower) || !!tagSpecific?.has(lower));
    if (!allowed || (URL_ATTRIBUTES.has(lower) && !isSafeUrl(value, lower === 'src'))) {
      element.removeAttribute(name);
      return;
    }
    if (lower === 'style') {
      const style = sanitizeStyle(value);
      if (style) {
        element.setAttribute(name, style);
      } else {
        element.removeAttribute(name);
      }
    }
  });
  // A link that opens a new tab must not hand the opener to the target window.
  if (tag === 'a' && element.hasAttribute('target')) {
    element.setAttribute('rel', 'noopener noreferrer');
  }
}

function sanitizeStyle(style: string): string {
  const kept: string[] = [];
  splitDeclarations(style).forEach(declaration => {
    const separator = declaration.indexOf(':');
    const property = declaration.slice(0, Math.max(separator, 0)).trim();
    const value = declaration.slice(separator + 1).trim();
    if (separator > 0 && property && value && isSafeCssDeclaration(property, value)) {
      kept.push(`${property}: ${value}`);
    }
  });
  return kept.join('; ');
}

function isSafeCssDeclaration(property: string, value: string): boolean {
  if (BLOCKED_CSS_PROPERTIES.has(property.toLowerCase())) {
    return false;
  }
  const compact = compactValue(value).toLowerCase();
  if (BLOCKED_CSS_VALUES.some(blocked => compact.includes(blocked))) {
    return false;
  }
  let index = compact.indexOf('url(');
  while (index >= 0) {
    const end = compact.indexOf(')', index);
    if (end < 0 || !isSafeUrl(compact.slice(index + 4, end).replace(/^['"]+|['"]+$/g, ''), true)) {
      return false;
    }
    index = compact.indexOf('url(', end + 1);
  }
  return true;
}

function splitDeclarations(style: string): string[] {
  const declarations: string[] = [];
  let buffer = '';
  let depth = 0;
  let quote = '';
  for (const c of style) {
    if (quote) {
      buffer += c;
      if (c === quote) {
        quote = '';
      }
      continue;
    }
    if (c === '"' || c === '\'') {
      quote = c;
    } else if (c === '(') {
      depth++;
    } else if (c === ')' && depth > 0) {
      depth--;
    } else if (c === ';' && depth === 0) {
      declarations.push(buffer);
      buffer = '';
      continue;
    }
    buffer += c;
  }
  declarations.push(buffer);
  return declarations;
}

function isSafeUrl(value: string, allowDataImage: boolean): boolean {
  const compact = compactValue(value);
  if (!compact) {
    return false;
  }
  if (compact[0] === '#' || compact[0] === '/') {
    return true;
  }
  const colon = compact.indexOf(':');
  const pathStart = compact.search(/[/?#]/);
  if (colon < 0 || (pathStart >= 0 && pathStart < colon)) {
    return true;
  }
  const scheme = compact.slice(0, colon).toLowerCase();
  return ALLOWED_URL_SCHEMES.has(scheme) || (allowDataImage && scheme === 'data' && DATA_IMAGE_URL.test(compact));
}

// Whitespace and control characters go before scheme detection, so "java\tscript:" still reads as javascript:.
function compactValue(value: string): string {
  return Array.from(value).filter(c => {
    const code = c.charCodeAt(0);
    return c.trim() !== '' && code > 0x1f && (code < 0x7f || code > 0x9f);
  }).join('');
}
